/**
 * Lee un archivo CSV como texto, detectando su codificación en vez de asumir siempre UTF-8.
 *
 * 🔴 Bug real confirmado en Mongo QA (2026-09-02): 469 fichas de la carga masiva del padrón
 * quedaron con el carácter de reemplazo Unicode "�" metido en el nombre (p. ej.
 * "MARIA MONTSERRAT SANCHEZ PE�A" en vez de "PEÑA"). Causa: `FileReader.readAsText(file)` sin
 * codificación decodifica los bytes como UTF-8 — y el CSV que exporta Excel en Windows para
 * locales en español/portugués/francés normalmente NO es UTF-8, es Windows-1252. Cualquier byte
 * de Windows-1252 que no forme una secuencia UTF-8 válida se reemplaza por "�", y esa "�" ya
 * corrompida es lo que terminaba persistido en Mongo — irrecuperable sin volver a cargar el
 * archivo original.
 *
 * Estrategia (la misma que usan los editores de texto para "adivinar" la codificación de un
 * archivo sin declarar una): con BOM UTF-8 explícito, se respeta. Sin BOM, se intenta decodificar
 * como UTF-8 en modo estricto (`fatal: true`); si CUALQUIER byte no es UTF-8 válido, se cae a
 * Windows-1252 — un superconjunto seguro de ASCII que cubre los acentos/eñes/cedillas de los 7
 * idiomas de la app, y que casi nunca decodifica una secuencia UTF-8 real como si fuera inválida.
 */
export async function readCsvFile(file: File): Promise<string> {
  // `FileReader.readAsArrayBuffer`, no `file.arrayBuffer()`: el segundo no existe en todos los
  // entornos de prueba (jsdom no siempre lo implementa), y el primero es el mismo API que ya usaba
  // este código antes del fix.
  const buffer = await new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
  const bytes = new Uint8Array(buffer);

  // BOM UTF-8 (EF BB BF): señal explícita, se respeta sin adivinar nada. `ignoreBOM: false`
  // deja que el decoder mismo se la coma, así el texto resultante no la arrastra.
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return new TextDecoder('utf-8').decode(bytes);
  }

  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('windows-1252').decode(bytes);
  }
}
